import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { LinkedInReviewContent } from "@/components/linkedin/LinkedInReviewContent";
import { LinkedInRevisaoComPerfil, PainelDaLeitura } from "@/components/linkedin/LinkedInRevisaoComPerfil";
import { linkedInApi } from "@/services/linkedin-api";

/**
 * Revisão sequencial de um lote (batch_id): percorre os jobs `review_ready` um a
 * um, aplicando (avança) ou pulando (joga pro fim). Espelha o fluxo da avaliação
 * de freelancer em sequência. A fila é um snapshot local: itens prontos DEPOIS
 * de abrir a tela não entram nela (revise o lote de novo pra pegá-los).
 */
export default function LinkedInBatchReviewPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fila local de jobIds a revisar + ponteiro. `null` = ainda não inicializada.
  const [queue, setQueue] = useState<string[] | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [appliedCount, setAppliedCount] = useState(0);

  // Volta pra tela de onde veio (lote ou listagem). Sem histórico dentro do
  // app (link direto, refresh), cai na listagem.
  const goBack = () => {
    const historyState = window.history.state as { idx?: number } | null;
    if ((historyState?.idx ?? 0) > 0) navigate(-1);
    else navigate("/linkedin");
  };

  // Jobs do lote — só pra montar a fila (não traz extracted_json).
  const batchQuery = useQuery({
    queryKey: ["linkedin-batch", batchId],
    queryFn: () => linkedInApi.listJobs({ batch_id: batchId!, limit: 300 }),
    enabled: !!batchId,
  });

  // Inicializa a fila uma vez, quando os jobs do lote chegam.
  useEffect(() => {
    if (queue === null && batchQuery.data) {
      const ids = batchQuery.data.jobs
        .filter((j) => j.status === "review_ready")
        .map((j) => j.id);
      setQueue(ids);
    }
  }, [queue, batchQuery.data]);

  // Mantém o índice dentro dos limites quando a fila encolhe (após aplicar).
  useEffect(() => {
    if (queue && queue.length > 0 && currentIndex >= queue.length) {
      setCurrentIndex(queue.length - 1);
    }
  }, [queue, currentIndex]);

  // Terminou (aplicou tudo que dava) => volta pra lista.
  useEffect(() => {
    if (queue && queue.length === 0 && appliedCount > 0) {
      toast({
        title: "Lote revisado",
        description: `${appliedCount} perfil(is) aplicado(s).`,
      });
      navigate("/linkedin");
    }
  }, [queue, appliedCount, navigate, toast]);

  const currentJobId =
    queue && queue.length > 0 ? queue[Math.min(currentIndex, queue.length - 1)] : null;

  const jobQuery = useQuery({
    queryKey: ["linkedin-job", currentJobId],
    queryFn: () => linkedInApi.getJob(currentJobId!),
    enabled: !!currentJobId,
    // O job anterior fica enquanto o próximo carrega só para o painel não fechar; a revisão espera o novo.
    placeholderData: keepPreviousData,
  });
  const carregandoJob = jobQuery.isLoading || jobQuery.isPlaceholderData;

  // Aplicado com sucesso: remove da fila e avança (o próximo entra no índice atual).
  const handleApplied = () => {
    setAppliedCount((c) => c + 1);
    setQueue((prev) => (prev ? prev.filter((id) => id !== currentJobId) : prev));
  };

  // Pular: joga o item atual pro fim da fila.
  const handleSkip = () => {
    if (!queue || queue.length <= 1) {
      toast({ title: "Nada para pular", description: "É o único item da fila." });
      return;
    }
    const id = queue[currentIndex];
    const rest = queue.filter((_, i) => i !== currentIndex);
    const next = [...rest, id];
    setQueue(next);
    // Se pulou o último, volta pro começo; senão o próximo já ocupa o índice atual.
    if (currentIndex >= next.length - 1) setCurrentIndex(0);
  };

  const total = queue?.length ?? 0;

  return (
    <div className="container mx-auto max-w-4xl p-6 space-y-4 xl:max-w-7xl">
      <Button variant="ghost" size="sm" onClick={goBack} className="gap-1.5 -ml-2">
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </Button>

      <div>
        <h1 className="text-2xl font-bold">Revisar lote em sequência</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {total > 0
            ? `Revisando ${Math.min(currentIndex + 1, total)} de ${total}` +
              (appliedCount > 0 ? ` · ${appliedCount} aplicado(s)` : "")
            : "Aplique ou pule cada perfil. Pular joga o item pro fim da fila."}
        </p>
      </div>

      {batchQuery.isLoading || queue === null ? (
        <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" />
          Carregando lote...
        </div>
      ) : batchQuery.isError ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-destructive">
            Não foi possível carregar o lote. {(batchQuery.error as Error)?.message}
          </CardContent>
        </Card>
      ) : total === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Nenhum perfil pronto para revisar neste lote.
          </CardContent>
        </Card>
      ) : carregandoJob ? (
        <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" />
          Carregando perfil...
        </div>
      ) : !jobQuery.data?.extracted_json ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            <p className="mb-4">
              Este perfil não tem dados para revisar
              {jobQuery.data ? ` (status: ${jobQuery.data.status}).` : "."}
            </p>
            <Button variant="secondary" onClick={handleSkip}>
              Pular
            </Button>
          </CardContent>
        </Card>
      ) : (
        <LinkedInRevisaoComPerfil job={jobQuery.data} painelNaPagina>
          <Card>
            <CardContent className="pt-6">
              <LinkedInReviewContent
                key={currentJobId}
                job={jobQuery.data}
                onClose={handleApplied}
                sequential={{
                  current: currentIndex + 1,
                  total,
                  onSkip: handleSkip,
                }}
              />
            </CardContent>
          </Card>
        </LinkedInRevisaoComPerfil>
      )}
      <PainelDaLeitura job={jobQuery.data} />
    </div>
  );
}
